import { useState } from 'react';
import { Lock, MoreVertical, UserPlus, VideoOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';
import { DailyCamIcon, DailyMicIcon } from './dailyTutorialMediaIcons';

const PANEL_BG = '#121A24';
const PANEL_BORDER = '#2B3F56';
const MUTED_TEXT = '#9aa0a6';

export type DailyCallSidePanel = 'people' | 'chat' | null;

type PeopleChatProps = {
	participantDisplayName: string;
	mockPatientDisplayName: string;
	micOn: boolean;
	camOn: boolean;
	hasCamera: boolean;
	hasMicrophone: boolean;
	activeTab: 'people' | 'chat';
	onTabChange: (tab: 'people' | 'chat') => void;
	onClose: () => void;
};

const PeopleListRow = ({
	name,
	isLocal,
	micOn,
	camOn,
	hasCamera,
	hasMicrophone,
}: {
	name: string;
	isLocal: boolean;
	micOn: boolean;
	camOn: boolean;
	hasCamera: boolean;
	hasMicrophone: boolean;
}) => {
	const micActive = hasMicrophone && micOn;
	const camActive = hasCamera && camOn;

	return (
		<div className="flex items-center justify-between gap-2 py-1">
			<span className="min-w-0 truncate text-sm text-white">
				{name}
				{isLocal ? (
					<span className="text-white/70"> (Você)</span>
				) : null}
			</span>
			<div className="flex shrink-0 items-center gap-1.5 text-[#9aa0a6]">
				{isLocal ? (
					<button
						type="button"
						tabIndex={-1}
						aria-hidden
						className="flex size-7 items-center justify-center rounded-md hover:bg-white/5"
					>
						<UserPlus className="size-4" strokeWidth={2.25} />
					</button>
				) : null}
				<span
					className={cn(
						'flex size-7 items-center justify-center rounded-full',
						!micActive && 'bg-[#ea4335]',
					)}
				>
					<DailyMicIcon muted={!micActive} className="size-3.5" />
				</span>
				{isLocal ? (
					<span
						className={cn(
							'flex size-7 items-center justify-center rounded-full',
							!camActive && 'bg-[#ea4335]',
						)}
					>
						<DailyCamIcon muted={!camActive} className="size-3.5" />
					</span>
				) : (
					<span className="flex size-7 items-center justify-center">
						<VideoOff
							className="size-3.5 text-[#9aa0a6]"
							strokeWidth={2.25}
						/>
					</span>
				)}
				{isLocal ? (
					<button
						type="button"
						tabIndex={-1}
						aria-hidden
						className="flex size-7 items-center justify-center rounded-md hover:bg-white/5"
					>
						<MoreVertical className="size-4" strokeWidth={2.25} />
					</button>
				) : null}
			</div>
		</div>
	);
};

const SidePanelTab = ({
	label,
	isActive,
	onClick,
}: {
	label: string;
	isActive: boolean;
	onClick: () => void;
}) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(
			'relative px-3 py-2.5 text-sm font-normal transition-colors',
			isActive ? 'text-white' : 'text-[#9aa0a6] hover:text-white/90',
		)}
	>
		{label}
		{isActive ? (
			<span
				className="absolute inset-x-2 bottom-0 h-0.5 rounded-full"
				style={{ backgroundColor: PRONTUARIO_TRAY_ACTIVE_COLOR }}
				aria-hidden
			/>
		) : null}
	</button>
);

const DailyCallDemonstracaoPeopleChatPanel = ({
	participantDisplayName,
	mockPatientDisplayName,
	micOn,
	camOn,
	hasCamera,
	hasMicrophone,
	activeTab,
	onTabChange,
	onClose,
}: PeopleChatProps) => {
	const [chatDraft, setChatDraft] = useState('');

	const selectTab = (tab: 'people' | 'chat') => {
		onTabChange(tab);
	};

	return (
		<aside
			className="flex h-full min-h-0 w-[280px] shrink-0 flex-col border-l text-white"
			style={{ backgroundColor: PANEL_BG, borderColor: PANEL_BORDER }}
		>
			<div
				className="flex shrink-0 items-center justify-between border-b pr-1"
				style={{ borderColor: PANEL_BORDER }}
			>
				<div className="flex items-center">
					<SidePanelTab
						label="Pessoas"
						isActive={activeTab === 'people'}
						onClick={() => selectTab('people')}
					/>
					<SidePanelTab
						label="Bate-papo"
						isActive={activeTab === 'chat'}
						onClick={() => selectTab('chat')}
					/>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="flex size-8 items-center justify-center rounded-md text-[#9aa0a6] transition-colors hover:bg-white/5 hover:text-white"
					aria-label="Fechar painel"
				>
					<X className="size-4" strokeWidth={2.25} />
				</button>
			</div>

			{activeTab === 'people' ? (
				<div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
					<p className="mb-3 text-xs" style={{ color: MUTED_TEXT }}>
						2 pessoas na chamada
					</p>
					<div className="space-y-1">
						<PeopleListRow
							name={mockPatientDisplayName}
							isLocal={false}
							micOn
							camOn={false}
							hasCamera
							hasMicrophone
						/>
						<PeopleListRow
							name={participantDisplayName}
							isLocal
							micOn={micOn}
							camOn={camOn}
							hasCamera={hasCamera}
							hasMicrophone={hasMicrophone}
						/>
					</div>
				</div>
			) : (
				<div className="flex min-h-0 flex-1 flex-col">
					<div className="min-h-0 flex-1" />
					<div
						className="shrink-0 border-t px-3 py-3"
						style={{ borderColor: PANEL_BORDER }}
					>
						<textarea
							value={chatDraft}
							onChange={(event) =>
								setChatDraft(event.target.value.slice(0, 256))
							}
							placeholder="Digite uma mensagem..."
							rows={2}
							className={cn(
								'w-full resize-none rounded-md border bg-transparent px-3 py-2',
								'text-sm text-white placeholder:text-[#9aa0a6]',
								'focus:outline-none focus:ring-1 focus:ring-[#8ab4f8]/40',
							)}
							style={{ borderColor: PANEL_BORDER }}
						/>
						<div
							className="mt-1.5 flex items-center gap-1 text-[11px]"
							style={{ color: MUTED_TEXT }}
						>
							<Lock
								className="size-3 shrink-0"
								strokeWidth={2.25}
							/>
							<span>Limite de 256 caracteres</span>
						</div>
					</div>
				</div>
			)}
		</aside>
	);
};

export { DailyCallDemonstracaoPeopleChatPanel };
